import * as Fingerprint from '@fingerprint/agent';

import config from 'constants/config';

const CACHE_KEY = 'gp_fingerprint';
// visitorId is only used by non-verified flows (sign-in, token refresh) where a stale value is
// harmless, so cache it long to minimise billable FingerprintJS identifications.
const VISITOR_TTL = 60 * 86400 * 1000; // 60 days in ms
// requestId is sent at sign-up and resolved server-side via the immediately-consistent
// /events/{requestId} endpoint, which retains events ~10 days. Keep the cached requestId well under
// that window so a reused one still resolves (and never falls back to the lagging /visitors lookup).
const REQUEST_TTL = 6 * 86400 * 1000; // 6 days in ms

let fpAgent = null;
let inFlightPromise = null;

const getFpAgent = () => {
  if (!fpAgent) {
    fpAgent = Fingerprint.start({
      apiKey: config.fingerprintToken,
      endpoints: 'https://f.getprospect.com',
    });
  }
  return fpAgent;
};

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
  } catch (error) {
    return null;
  }
};

// Performs ONE identification (one billable fp.get()), caches both ids with their own TTLs, and
// returns { visitorId, requestId }. Concurrent callers share a single in-flight request so a page
// never triggers more than one identification at a time.
const identify = async () => {
  if (!inFlightPromise) {
    inFlightPromise = (async () => {
      const result = await getFpAgent().get();
      const visitorId = result.visitorId || result.visitor_id;
      const requestId = result.requestId || result.request_id || result.event_id;
      if (visitorId) {
        const now = Date.now();
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            visitorId,
            requestId,
            visitorExpiresAt: now + VISITOR_TTL,
            requestExpiresAt: now + REQUEST_TTL,
          }),
        );
      }
      return { visitorId, requestId };
    })().finally(() => {
      inFlightPromise = null;
    });
  }
  return inFlightPromise;
};

// Cached visitorId for non-critical flows (sign-in, token refresh). Re-identifies only every 60 days.
export const getFingerPrint = async () => {
  try {
    const cached = readCache();
    if (cached?.visitorId && cached.visitorExpiresAt > Date.now()) {
      return cached.visitorId;
    }
    return (await identify()).visitorId;
  } catch (error) {
    console.error('FingerprintJS error:', error);
    return null;
  }
};

// Sign-up / Google-auth identity: returns { visitorId, requestId } for server-side verification via
// the lag-proof /events/{requestId} endpoint. Reuses the cached requestId while it is still fresh
// enough to resolve there, and only triggers a new identification on a miss - so a signing-up visitor
// is never re-identified redundantly (keeps FingerprintJS events-per-visitor ~1).
export const getSignupFingerPrint = async () => {
  try {
    const cached = readCache();
    if (cached?.visitorId && cached?.requestId && cached.requestExpiresAt > Date.now()) {
      return { visitorId: cached.visitorId, requestId: cached.requestId };
    }
    return await identify();
  } catch (error) {
    console.error('FingerprintJS error:', error);
    return { visitorId: null, requestId: null };
  }
};
